/**
 * Las notas de las láminas «Solo referencia» pegadas a los conceptos (se pegan solas, sin botón).
 *
 *  - `NotasDelDetalle`: en el panel del concepto (paso 2, adicionales, instalaciones, «Generar con IA»).
 *    Con `notasPegadas` (el renglón ya está en la hoja) muestra las suyas, cada una con ✕ para quitarla.
 *    Sin eso es la vista previa: lo que se le va a pegar al agregarlo (la misma regla que lo guarda).
 *    En los dos casos avisa, en amarillo, si la lámina describe el MISMO elemento con otro material.
 *  - `IndicadorNotas`: la marca «N REF.» de la hoja; al tocarla se ven las notas y se pueden quitar.
 *  - `ResumenNotasDeLaHoja`: arriba de la hoja, «Especificaciones de L lámina(s) en N conceptos».
 *
 * $0, sin IA. Si la consulta falla no se pinta nada (es una ayuda: no bloquea agregar el concepto).
 */
import { useEffect, useState } from 'react';
import { Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { DismissRegular, TagRegular } from '@fluentui/react-icons';
import type { EspecificacionVinculada, NotaDeLaminaDto } from '@supernova/shared';
import { especificacionesParaGenerar } from '../../api/planos.js';
import { marcaDeRenglon } from './marcaDeRenglon.js';

const PAUSA_MS = 400;

/** «Detalles constructivos D-01 (prueba).dxf» → «Detalles constructivos D-01 (prueba)». */
export function nombreDeLamina(archivo: string | null | undefined): string {
  const a = (archivo ?? '').trim();
  return a ? a.replace(/\.[a-z0-9]{2,4}$/i, '') : 'de referencia';
}

/** Recorta un texto largo a la primera parte, sin cortar palabras. */
function corto(texto: string, max = 70): string {
  const t = texto.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cortado = t.slice(0, max);
  const i = cortado.lastIndexOf(' ');
  return `${(i > max * 0.6 ? cortado.slice(0, i) : cortado).replace(/[\s,;.:]+$/, '')}…`;
}

/** Lámina de una nota pegada (el id es `${fileId}#…`). */
function laminaDe(detalleId: string): string {
  const i = detalleId.indexOf('#');
  return i < 0 ? detalleId : detalleId.slice(0, i);
}

function LineaNota({
  archivo,
  texto,
  via,
  onQuitar,
  quitando,
}: {
  archivo: string | null;
  texto: string;
  via?: EspecificacionVinculada['via'];
  onQuitar?: () => void;
  quitando?: boolean;
}) {
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11.5, lineHeight: 1.4, color: 'var(--text-primary)' }}>
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
        <b style={{ color: 'var(--text-secondary)' }}>
          {via === 'general' ? 'Nota general de' : 'Del detalle'} {nombreDeLamina(archivo)}:
        </b>{' '}
        {texto}
      </span>
      {onQuitar && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onQuitar();
          }}
          disabled={quitando}
          title="Quitar esta nota de este concepto"
          aria-label="Quitar esta nota de este concepto"
          style={{
            flexShrink: 0, border: 'none', background: 'transparent', cursor: quitando ? 'default' : 'pointer',
            color: 'var(--text-tertiary)', padding: 2, lineHeight: 0, borderRadius: 4,
          }}
        >
          <DismissRegular style={{ fontSize: 13 }} />
        </button>
      )}
    </li>
  );
}

export function NotasDelDetalle({
  projectId,
  texto,
  nombre,
  notasPegadas,
  onQuitar,
}: {
  projectId: string | null | undefined;
  /** El concepto (su descripción): con él se calcula la vista previa y la contradicción. */
  texto: string;
  /** Capa o bloque seleccionado (se descarta al comparar). */
  nombre?: string | null;
  /** El renglón ya está en la hoja: sus notas pegadas. Sin esto, vista previa. */
  notasPegadas?: EspecificacionVinculada[];
  /** Quitar una nota pegada (solo con `notasPegadas`). Devuelve cuando terminó. */
  onQuitar?: (nota: EspecificacionVinculada) => Promise<void>;
}) {
  const [previa, setPrevia] = useState<NotaDeLaminaDto[]>([]);
  const [contradicciones, setContradicciones] = useState<NotaDeLaminaDto[]>([]);
  const [quitando, setQuitando] = useState<string | null>(null);
  const t = texto.trim();

  useEffect(() => {
    if (!projectId || !t) {
      setPrevia([]);
      setContradicciones([]);
      return;
    }
    const ctrl = new AbortController();
    const id = window.setTimeout(() => {
      especificacionesParaGenerar(projectId, t, nombre ?? null, ctrl.signal)
        .then((r) => {
          if (ctrl.signal.aborted) return;
          setPrevia(r.notas ?? []);
          setContradicciones(r.contradicciones ?? []);
        })
        .catch(() => {
          if (ctrl.signal.aborted) return;
          setPrevia([]);
          setContradicciones([]);
        });
    }, PAUSA_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(id);
    };
  }, [projectId, t, nombre]);

  const pegadas = notasPegadas ?? null;
  const hayNotas = pegadas ? pegadas.length > 0 : previa.length > 0;
  if (!hayNotas && contradicciones.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {hayNotas && (
        <div
          style={{
            display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 10px', borderRadius: 8,
            border: '1px solid var(--border-subtle)', background: 'var(--bg-page)',
          }}
        >
          {!pegadas && (
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>Al agregarlo se le pega:</span>
          )}
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pegadas
              ? pegadas.map((n) => (
                  <LineaNota
                    key={n.detalleId}
                    archivo={n.archivo}
                    texto={n.texto}
                    via={n.via}
                    quitando={quitando === n.detalleId}
                    onQuitar={
                      onQuitar
                        ? () => {
                            setQuitando(n.detalleId);
                            void onQuitar(n).finally(() => setQuitando(null));
                          }
                        : undefined
                    }
                  />
                ))
              : previa.map((n) => <LineaNota key={n.texto} archivo={n.archivo} texto={n.texto} via={n.via} />)}
          </ul>
        </div>
      )}
      {contradicciones.map((n) => (
        <div
          key={n.texto}
          role="note"
          style={{
            padding: '8px 10px', borderRadius: 8, border: '1px solid var(--warn)', background: 'var(--warn-weak)',
            fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-primary)',
          }}
        >
          Elegiste «{corto(t, 60)}»; el detalle {nombreDeLamina(n.archivo)} dice «{corto(n.texto, 80)}».
        </div>
      ))}
    </div>
  );
}

/** La marca «N REF.» de la hoja: al tocarla, las notas pegadas, cada una con ✕. */
export function IndicadorNotas({
  specs,
  onQuitar,
}: {
  specs: EspecificacionVinculada[];
  onQuitar?: (nota: EspecificacionVinculada) => Promise<void>;
}) {
  const [quitando, setQuitando] = useState<string | null>(null);
  if (!specs || specs.length === 0) return null;
  return (
    <Popover withArrow positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${specs.length} ${specs.length === 1 ? 'nota' : 'notas'} de las láminas de detalle`}
          title="Notas de las láminas de detalle"
          style={{ ...marcaDeRenglon('var(--accent)'), fontFamily: 'inherit', background: 'transparent', cursor: 'pointer' }}
        >
          <TagRegular style={{ fontSize: 11 }} />{specs.length} ref.
        </button>
      </PopoverTrigger>
      <PopoverSurface style={{ maxWidth: 420, padding: 12 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Se pegaron solas de las láminas «Solo referencia». No cambian la cantidad ni el precio.
          </span>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {specs.map((n) => (
              <LineaNota
                key={n.detalleId}
                archivo={n.archivo}
                texto={n.texto}
                via={n.via}
                quitando={quitando === n.detalleId}
                onQuitar={
                  onQuitar
                    ? () => {
                        setQuitando(n.detalleId);
                        void onQuitar(n).finally(() => setQuitando(null));
                      }
                    : undefined
                }
              />
            ))}
          </ul>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

/** «Especificaciones de L lámina(s) en N conceptos» (solo si N > 0). */
export function resumenNotasDeLaHoja(renglones: { specRefs: EspecificacionVinculada[] }[]): string | null {
  const conNotas = renglones.filter((r) => (r.specRefs ?? []).length > 0);
  if (conNotas.length === 0) return null;
  const laminas = new Set(conNotas.flatMap((r) => r.specRefs.map((s) => laminaDe(s.detalleId))));
  const L = laminas.size;
  const N = conNotas.length;
  return `Especificaciones de ${L} ${L === 1 ? 'lámina' : 'láminas'} en ${N} ${N === 1 ? 'concepto' : 'conceptos'}`;
}
